import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import { wellFlowingTemperatureForMD } from "@/libs/calculations/pressureDropCorrelations";
import {
  buildIntervalCasingGeometry,
  calculateSensorAndMotorCasingFriction
} from "../../casingFrictionCalculations";
import type { FluidPropertiesPipelineInputs } from "../types";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";

// Sensor -> motor. Without a sensor the motor is the bottom of equipment.
export const calculateSensorToMotor = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    hasSensor: boolean;
    getPressureAtReferenceTVDPSIA: (referenceTVDFt: number) => number;
    perfsToBottomOfEquipmentCasingFrictionPSIA: number;
    downholeBottomOfEquipmentTemperatureF: number;
    fluidPropertiesAtBottomOfEquipmentContext: FluidPropertiesContext;
    casingFrictionLossPSIA: number;
  }
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    includeCasingFrictionLoss,
    entryRows,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    bottomOfSensorMDFt,
    bottomOfSensorTVDFt,
    sensorOuterDiameterInches,
    bottomOfMotorMDFt,
    bottomOfMotorTVDFt,
    motorCrossSectionFt2,
    effectiveCasingInteriorDiameterAtMotorIn,
    effectiveCasingInteriorDiameterAtSensorIn,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  } = inputs;
  const {
    hasSensor,
    getPressureAtReferenceTVDPSIA,
    perfsToBottomOfEquipmentCasingFrictionPSIA,
    downholeBottomOfEquipmentTemperatureF,
    fluidPropertiesAtBottomOfEquipmentContext,
    casingFrictionLossPSIA: startingCasingFrictionLossPSIA
  } = prev;
  let casingFrictionLossPSIA = startingCasingFrictionLossPSIA;
  let sensorAndMotorCasingFrictionPSIA = 0;

  let wellFlowingPressureAtSensorPSIA;
  let downholeSensorTemperatureF;
  let fluidPropertiesAtSensorContext;
  let wellFlowingPressureAtMotorPSIA;
  let downholeMotorTemperatureF;
  let fluidPropertiesAtMotorContext;

  // If it has a sensor then we know we can use the fluid properties at bottom of equipment
  if (hasSensor) {
    wellFlowingPressureAtSensorPSIA =
      getPressureAtReferenceTVDPSIA(bottomOfSensorTVDFt) -
      perfsToBottomOfEquipmentCasingFrictionPSIA;
    downholeSensorTemperatureF = downholeBottomOfEquipmentTemperatureF;
    fluidPropertiesAtSensorContext = fluidPropertiesAtBottomOfEquipmentContext;

    const sensorAndMotorPressureProperties =
      calculateSensorAndMotorCasingFriction({
        correlations,
        bottomOfSensorMDFt,
        bottomOfSensorTVDFt,
        bottomOfMotorMDFt,
        bottomOfMotorTVDFt,
        entryRows,
        fluidPropertiesAtSensorContext,
        surfaceTemperatureF: basicData.surfaceTemperatureF,
        bottomholeTemperatureF: basicData.bottomholeTemperatureF,
        casingGeometry: buildIntervalCasingGeometry(
          bottomOfSensorMDFt,
          bottomOfMotorMDFt,
          topCasingInteriorDiameterIn,
          topCasingLengthFt,
          bottomCasingInteriorDiameterIn,
          bottomCasingLengthFt,
          effectiveCasingInteriorDiameterAtSensorIn,
          sensorOuterDiameterInches
        ),
        bottomCasingPressurePSIA: basicData.bottomCasingPressurePSIA,
        producingBottomholePressurePSIA:
          basicData.producingBottomholePressurePSIA
      });

    sensorAndMotorCasingFrictionPSIA =
      sensorAndMotorPressureProperties.frictionPressurePSIA;
    let sensorAndMotorAppliedFrictionPSIA = 0;
    if (includeCasingFrictionLoss) {
      sensorAndMotorAppliedFrictionPSIA = sensorAndMotorCasingFrictionPSIA;
      casingFrictionLossPSIA += sensorAndMotorAppliedFrictionPSIA;
    }
    wellFlowingPressureAtMotorPSIA =
      getPressureAtReferenceTVDPSIA(bottomOfMotorTVDFt) -
      sensorAndMotorAppliedFrictionPSIA;

    downholeMotorTemperatureF = wellFlowingTemperatureForMD(
      bottomOfMotorMDFt,
      basicData.datumPointFt,
      basicData.bottomholeTemperatureF,
      basicData.surfaceTemperatureF
    );
    fluidPropertiesAtMotorContext = getFluidPropertiesContext(
      basicData,
      correlations,
      wellFlowingPressureAtMotorPSIA,
      downholeMotorTemperatureF,
      effectiveCasingInteriorDiameterAtMotorIn,
      desiredFlowRates,
      fluidPropertiesAtSensorContext.flowRatesInSitu,
      fluidPropertiesAtSensorContext.flowRatePrerequisites,
      customFluidProperties,
      customViscosityProperties,
      bubblePointPressurePSIA,
      dewPointPressurePSIA,
      motorCrossSectionFt2,
      0,
      fluidPropertiesOverrides,
      condensateApi
    );
  } else {
    // If there is no sensor, the sensor context is intentionally undefined.
    wellFlowingPressureAtSensorPSIA = undefined;
    downholeSensorTemperatureF = undefined;
    fluidPropertiesAtSensorContext = undefined;

    wellFlowingPressureAtMotorPSIA =
      getPressureAtReferenceTVDPSIA(bottomOfMotorTVDFt) -
      perfsToBottomOfEquipmentCasingFrictionPSIA;
    downholeMotorTemperatureF = downholeBottomOfEquipmentTemperatureF;
    fluidPropertiesAtMotorContext = fluidPropertiesAtBottomOfEquipmentContext;
  }
  return {
    wellFlowingPressureAtSensorPSIA,
    downholeSensorTemperatureF,
    fluidPropertiesAtSensorContext,
    wellFlowingPressureAtMotorPSIA,
    downholeMotorTemperatureF,
    fluidPropertiesAtMotorContext,
    sensorAndMotorCasingFrictionPSIA,
    casingFrictionLossPSIA
  };
};
